import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card, Empty, Spin, Button, Popconfirm, message, Tabs, Space } from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined, AppstoreAddOutlined, DownloadOutlined, FileTextOutlined, FileImageOutlined } from '@ant-design/icons';
import { exportToCSV, exportElementAsImage } from '@/utils/exportUtils';
import { executeCustomAnalytics } from '@/redux/rtk/features/CRM/analytics/analyticsSlice';
import { Dropdown } from 'antd';
import { useTranslation } from "react-i18next";
import { fetchSavedAnalytics, deleteAnalytics } from '@/redux/rtk/features/CRM/analytics/analyticsSlice';
import CreateAnalyticsModal from './CreateAnalyticsModal';
import ViewAnalyticsModal from './ViewAnalyticsModal';
import CreatePageModal from './CreatePageModal';

const MyAnalyticsDashboard = ({ onDrillDown }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { savedAnalytics, loadingSaved } = useSelector((state) => state.analytics);
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [isPageModalVisible, setIsPageModalVisible] = useState(false);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [selectedAnalytic, setSelectedAnalytic] = useState(null);
    const [editingAnalytic, setEditingAnalytic] = useState(null);
    const [activeTabKey, setActiveTabKey] = useState('general');

    useEffect(() => {
        dispatch(fetchSavedAnalytics({ includePublic: true }));
    }, [dispatch]);

    // Separate Pages (Tabs) from Widgets
    // Pages are analytics with chartType === 'page_definition'
    const pages = savedAnalytics?.filter(a => a.chartType === 'page_definition') || [];
    const widgets = savedAnalytics?.filter(a => a.chartType !== 'page_definition') || [];

    // Group Widgets by Page ID
    // If no pageId is present in chartConfig, it belongs to 'general'
    const getWidgetsForPage = (pageId) => {
        if (pageId === 'general') {
            return widgets.filter(w => !w.chartConfig?.pageId);
        }
        return widgets.filter(w => w.chartConfig?.pageId === pageId);
    };

    const handleCreateWidgetClick = () => {
        setEditingAnalytic(null);
        setIsCreateModalVisible(true);
    };

    const handleCreatePageClick = () => {
        setIsPageModalVisible(true);
    };

    const handleCreateModalClose = () => {
        setIsCreateModalVisible(false);
        setEditingAnalytic(null);
        dispatch(fetchSavedAnalytics({ includePublic: true }));
    };

    const handlePageModalClose = () => {
        setIsPageModalVisible(false);
        dispatch(fetchSavedAnalytics({ includePublic: true }));
    };

    const handleViewClick = (analytic) => {
        setSelectedAnalytic(analytic);
        setIsViewModalVisible(true);
    };

    const handleEditClick = (analytic) => {
        setEditingAnalytic(analytic);
        setIsCreateModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await dispatch(deleteAnalytics(id)).unwrap();
            message.success(t('success.deleted'));
        } catch (error) {
            console.error('Failed to delete item:', error);
            message.error(t('errors.somethingWentWrong'));
        }
    };

    const handleViewModalClose = () => {
        setIsViewModalVisible(false);
        setSelectedAnalytic(null);
    };

    const handleExportAnalytic = async (analytic) => {
        try {
            const config = {
                dataSource: analytic.dataSource,
                filters: analytic.filters ? (typeof analytic.filters === 'string' ? JSON.parse(analytic.filters) : analytic.filters) : {},
                groupBy: analytic.chartConfig?.groupBy,
                aggregation: analytic.chartConfig?.aggregation,
                timeRange: analytic.chartConfig?.timePeriod,
            };
            const result = await dispatch(executeCustomAnalytics(config)).unwrap();
            exportToCSV(result, analytic.name || 'custom_analytics');
            message.success(t('success.saved'));
        } catch (error) {
            console.error('Export failed:', error);
            message.error(t('errors.somethingWentWrong'));
        }
    };

    const handleExportImage = (analyticId, name) => {
        const elementId = `widget-card-${analyticId}`;
        exportElementAsImage(elementId, name || 'analytic');
    };

    const renderWidgetsGrid = (pageId) => {
        const pageWidgets = getWidgetsForPage(pageId);

        if (pageWidgets.length === 0) {
            return (
                <Empty
                    description={t('analytics.noAnalyticsInTab')}
                    style={{ padding: '40px 0' }}
                >
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateWidgetClick}>
                        {t('analytics.addAnalyticsWidget')}
                    </Button>
                </Empty>
            );
        }

        return (
            <div>
                <div style={{ marginBottom: '16px', textAlign: 'right' }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateWidgetClick}>
                        {t('analytics.addWidget')}
                    </Button>
                </div>
                <Row gutter={[16, 16]}>
                    {pageWidgets.map((analytic) => (
                        <Col span={12} key={analytic.id}>
                            <Card
                                id={`widget-card-${analytic.id}`}
                                title={analytic.name}
                                extra={
                                    <Space size={0}>
                                        <Button
                                            key="view"
                                            type="text"
                                            icon={<EyeOutlined />}
                                            onClick={() => handleViewClick(analytic)}
                                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        />
                                        <Button
                                            key="edit"
                                            type="text"
                                            icon={<EditOutlined />}
                                            onClick={() => handleEditClick(analytic)}
                                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        />
                                        <Popconfirm
                                            key="delete"
                                            title={t('analytics.deleteWidgetConfirm')}
                                            onConfirm={() => handleDelete(analytic.id)}
                                            okText={t('common.yes')}
                                            cancelText={t('common.no')}
                                        >
                                            <Button
                                                type="text"
                                                danger
                                                icon={<DeleteOutlined />}
                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            />
                                        </Popconfirm>
                                    </Space>
                                }
                            >
                                <p className="text-gray-500 mb-2">{analytic.description}</p>
                                <div className="text-xs text-gray-400">
                                    {analytic.chartType} • {analytic.dataSource} • {analytic.chartConfig?.timePeriod?.replace(/_/g, ' ') || 'All Time'}
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </div>
        );
    };

    const tabItems = [
        // Default General Tab
        {
            key: 'general',
            label: t('analytics.general'),
            children: renderWidgetsGrid('general'),
        },
        // User Created Tabs
        ...pages.map(page => ({
            key: String(page.id),
            label: (
                <span className="flex items-center gap-2">
                    {page.name}
                    <Popconfirm
                        title={t('analytics.deletePageConfirm')}
                        onConfirm={(e) => {
                            e.stopPropagation();
                            handleDelete(page.id);
                        }}
                        onCancel={(e) => e.stopPropagation()}
                        okText={t('common.yes')}
                        cancelText={t('common.no')}
                    >
                        <DeleteOutlined
                            className="text-red-400 hover:text-red-600 ml-2"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </Popconfirm>
                </span>
            ),
            children: renderWidgetsGrid(page.id),
        }))
    ];

    if (loadingSaved) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>{t('analytics.myCustomDashboards')}</h2>
            </div>

            <Tabs
                type="card"
                activeKey={activeTabKey}
                onChange={setActiveTabKey}
                tabBarExtraContent={
                    <Button
                        type="dashed"
                        icon={<AppstoreAddOutlined />}
                        onClick={handleCreatePageClick}
                    >
                        {t('analytics.newPage')}
                    </Button>
                }
                items={tabItems}
            />

            <CreateAnalyticsModal
                visible={isCreateModalVisible}
                onClose={handleCreateModalClose}
                editingAnalytic={editingAnalytic}
                pageId={activeTabKey === 'general' ? null : Number(activeTabKey)}
            />

            <CreatePageModal
                visible={isPageModalVisible}
                onClose={handlePageModalClose}
            />

            <ViewAnalyticsModal
                visible={isViewModalVisible}
                onClose={handleViewModalClose}
                analytic={selectedAnalytic}
                onDrillDown={onDrillDown}
            />
        </div>
    );
};

export default MyAnalyticsDashboard;
